/**
 * API 客户端
 * 统一的 HTTP 请求处理，包含认证令牌管理和错误处理
 */

import { API_CONFIG } from '../config/api';

// 存储键名
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

/**
 * API 客户端类
 */
class ApiClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.sessionExpiredTimer = null;

    // 熔断器状态 (Circuit Breaker)
    this.failureCount = 0;
    this.maxFailures = 3;
    this.pauseDuration = 30000; // 熔断时长：30秒
    this.pauseUntil = 0; // 暂停请求直到该时间点
  }

  /**
   * 重置熔断器状态
   */
  resetCircuit() {
    this.failureCount = 0;
    this.pauseUntil = 0;
  }

  /**
   * 处理请求失败，增加错误计数并在必要时触发熔断
   */
  handleFailure() {
    this.failureCount++;
    if (this.failureCount >= this.maxFailures) {
      this.pauseUntil = Date.now() + this.pauseDuration;
      console.warn(`[CircuitBreaker] 连续失败 ${this.failureCount} 次，触发熔断。暂停请求 ${this.pauseDuration / 1000} 秒。`);
    }
  }

  /**
   * 获取存储的访问令牌
   */
  getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * 获取存储的刷新令牌
   */
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * 保存令牌到本地存储
   */
  setTokens(accessToken, refreshToken = null) {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * 清除所有认证信息
   */
  clearAuth() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('share_token');
  }

  /**
   * 保存用户信息
   */
  setUser(user) {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  }

  /**
   * 获取存储的用户信息
   */
  getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * 构建请求头
   */
  buildHeaders(customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    // 如果有分享令牌，添加到请求头
    const shareToken = localStorage.getItem('share_token');
    if (shareToken) {
      headers['X-Share-Token'] = shareToken;
    }

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 添加当前用户信息到请求头（仅包含安全的 ASCII 字符，避免非 ISO-8859-1 字符）
    const user = this.getUser();
    if (user && user.username) {
      // 检查用户名是否只包含 ASCII 字符，否则不添加到请求头
      if (/^[\x00-\x7F]*$/.test(user.username)) {
        headers['X-User'] = user.username;
      }
      // 如果需要传输用户信息，后端可以使用认证令牌来识别用户身份
    }

    return headers;
  }

  /**
   * 发送请求
   */
  async request(endpoint, options = {}) {
    // 检查熔断器状态
    if (Date.now() < this.pauseUntil) {
      const waitSeconds = Math.ceil((this.pauseUntil - Date.now()) / 1000);
      throw new ApiError(`系统繁忙，请在 ${waitSeconds} 秒后重试 :)`, -1);
    }

    const url = `${this.baseURL}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers: this.buildHeaders(options.headers),
        signal: controller.signal,
        credentials: 'include',
      });

      clearTimeout(timeoutId);

      // 请求成功，重置熔断器
      if (response.ok) {
        this.resetCircuit();
      } else if (response.status >= 500 || response.status === 408) {
        // 服务端错误或超时，增加失败计数
        this.handleFailure();
      }

      // 处理 401 未授权错误 - 尝试刷新令牌
      if (response.status === 401) {
        const refreshed = await this.handleUnauthorized();
        if (refreshed) {
          // 重试原请求
          return this.request(endpoint, options);
        }
        // 刷新失败，显示提示并延迟跳转
        this.handleSessionExpired();
        throw new ApiError('认证已过期，3秒后将跳转到登录页面...', 401);
      }

      // 解析响应
      const data = await this.parseResponse(response);

      // 处理非成功状态码
      if (!response.ok) {
        throw new ApiError(
          data.error || data.message || '系统休整中，请稍等 :)',
          response.status,
          data
        );
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // 网络错误或超时，增加失败计数
      this.handleFailure();

      if (error.name === 'AbortError') {
        throw new ApiError('请求超时', 408);
      }

      if (error instanceof ApiError) {
        throw error;
      }

      // 网络错误
      throw new ApiError(
        error.message || '网络连接失败',
        0,
        { originalError: error }
      );
    }
  }

  /**
   * 解析响应体
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.text();
  }

  /**
   * 处理 401 未授权 - 尝试刷新令牌
   */
  async handleUnauthorized() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearAuth();
      return false;
    }

    // 如果已经在刷新中，等待刷新完成
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }

    this.isRefreshing = true;

    try {
      // 使用相对路径，通过 Vite proxy 代理
      const refreshUrl = this.baseURL ? `${this.baseURL}/api/auth/refresh` : '/api/auth/refresh';
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        this.setTokens(data.access_token, data.refresh_token);
        
        // 通知所有等待的请求
        this.refreshSubscribers.forEach((callback) => callback(true));
        this.refreshSubscribers = [];
        
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    } finally {
      this.isRefreshing = false;
    }

    // 刷新失败，清除认证信息
    this.clearAuth();
    this.refreshSubscribers.forEach((callback) => callback(false));
    this.refreshSubscribers = [];
    
    return false;
  }

  /**
   * 处理会话过期 - 延迟3秒后清除认证并跳转到登录页
   */
  handleSessionExpired() {
    // 防止重复触发
    if (this.sessionExpiredTimer) {
      return;
    }
    
    // 3秒后清除认证信息，触发自动跳转到登录页
    this.sessionExpiredTimer = setTimeout(() => {
      this.clearAuth();
      this.sessionExpiredTimer = null;
      // 强制刷新页面以确保状态重置（备用方案）
      // window.location.reload();
    }, 3000);
  }

  /**
   * GET 请求
   */
  get(endpoint, params = {}) {
    const queryString = this.buildQueryString(params);
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  /**
   * POST 请求
   */
  post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT 请求
   */
  put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE 请求
   */
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * 构建查询字符串
   */
  buildQueryString(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });
    return searchParams.toString();
  }
}

/**
 * API 错误类
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  /**
   * 是否是认证错误
   */
  isAuthError() {
    return this.status === 401;
  }

  /**
   * 是否是权限错误
   */
  isPermissionError() {
    return this.status === 403;
  }

  /**
   * 是否是验证错误
   */
  isValidationError() {
    return this.status === 400;
  }

  /**
   * 是否是网络错误
   */
  isNetworkError() {
    return this.status === 0;
  }
}

// 创建单例实例
const apiClient = new ApiClient();

export default apiClient;
